/**
 * AppErrorBoundary — the outermost render-time safety net.
 *
 * Wraps the entire app tree at the composition root (`main.tsx`), one
 * level inside `StrictMode` and outside every provider, router, and
 * route-level boundary. Its job is the last-resort case: an exception
 * thrown *outside* any `PortErrorBoundary`-guarded route subtree — a
 * provider construction failure, a router-level throw, a bug in the
 * shell chrome (TopBar / PrimaryNav / Layout) — which the per-route
 * `PortErrorBoundary` instances (mounted by `RouteRegistry` per the
 * `portBoundary` descriptor flag) cannot catch because they sit *below*
 * this point in the tree. The two boundary layers are complementary,
 * not redundant: route boundaries keep a single broken content route
 * from taking the shell hostage and surface the offending port;
 * this boundary keeps a shell-level or provider-level throw from
 * collapsing the page to React's blank default error UI.
 *
 * **Class component — the project's documented Error Boundary
 * exception.** React 19 still exposes error-boundary behaviour only
 * through the class lifecycle (`static getDerivedStateFromError`,
 * `componentDidCatch`); there is no functional equivalent in the stable
 * API and `react-error-boundary` is not (and per task constraints will
 * not be) a dependency. `PortErrorBoundary.tsx` documents the same
 * exception for the route layer; this file is its app-level twin
 * (R1 — classes only where Error Boundary compatibility makes that the
 * smallest safe choice; R20 — Error Boundaries for render-time
 * failures).
 *
 * **Fallback posture.** The boundary sits *above* `PortsProvider` /
 * `BrowserRouter`, so it cannot use `useTranslate` (no provider in
 * scope) and must not depend on the router. The fallback is therefore
 * a static, self-contained Finnish-first surface (FR-040): a
 * `role="alert"` region with a heading + short recovery sentence and a
 * full-reload affordance (`window.location.reload()` — the only
 * recovery that does not assume a working React tree). It carries no
 * design-token classes that depend on `tokens.css` having loaded,
 * using inline styles for the few primitives it needs so the fallback
 * renders even if the stylesheet pipeline is what failed (R45's
 * "genuinely tiny dynamic primitives" carve-out — a last-resort error
 * surface is exactly that).
 *
 * **componentDidCatch — intentionally empty.** Mirrors
 * `PortErrorBoundary`: Cluster 1 has no observability port; the empty
 * override signals "this hook is known and deliberately unused yet"
 * and gives a future observability adapter a one-file wiring seam.
 *
 * **No self-retry.** Once caught, the fallback stays until the user
 * triggers a full reload. There is no `key`-reset path because there
 * is nothing above this boundary to remount the tree, and a soft retry
 * cannot assume the failed provider/shell will succeed on a second
 * render.
 */

import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';

export interface AppErrorBoundaryProps {
  readonly children: ReactNode;
}

type AppErrorBoundaryState = { readonly crashed: boolean };

const overlayStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
  minHeight: '100vh',
  padding: '1.5rem',
  textAlign: 'center',
  fontFamily: 'system-ui, sans-serif',
};

const buttonStyle: CSSProperties = {
  cursor: 'pointer',
  borderRadius: '0.5rem',
  border: '1px solid currentColor',
  padding: '0.5rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  background: 'transparent',
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { crashed: false };

  static getDerivedStateFromError(_error: unknown): AppErrorBoundaryState {
    // `_error` is intentionally discarded here — the fallback is static
    // and self-contained (it cannot use the error message without a
    // provider in scope). The explicit underscored parameter mirrors
    // `componentDidCatch`'s convention and signals the discard is
    // deliberate, not an oversight.
    return { crashed: true };
  }

  override componentDidCatch(_error: unknown, _info: ErrorInfo): void {
    // Intentionally empty in Cluster 1 — see file header. A future
    // observability adapter logs here.
  }

  override render(): ReactNode {
    if (!this.state.crashed) {
      return this.props.children;
    }
    return (
      <div role="alert" style={overlayStyle}>
        <h1 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>Jokin meni vikaan</h1>
        <p style={{ fontSize: '0.875rem', margin: 0, maxWidth: '28rem' }}>
          Sovelluksessa tapahtui odottamaton virhe. Lataa sivu uudelleen jatkaaksesi.
        </p>
        <button
          type="button"
          style={buttonStyle}
          onClick={() => {
            window.location.reload();
          }}
        >
          Lataa uudelleen
        </button>
      </div>
    );
  }
}
